import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Alert, App as AntdApp, Segmented, Tabs} from "antd";
import {Button, FontAwesomeIcon, PageHeader} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useGetTrendsQuery} from "~/reduxs/api/trendsApiSlice";
import TrendsTable from "../components/TrendsTable";
import TrendsHistoryPanel from "../components/TrendsHistoryPanel";
import TrendsSaveModal from "../components/TrendsSaveModal";
import TrendsKeywordModal from "../components/TrendsKeywordModal";

/** Fallback danh sách tab khi chưa có response (BE trả đầy đủ trong data.tabs). */
const DEFAULT_TABS = [
	{value: 'VN', label: 'Việt Nam'},
	{value: 'KR', label: 'Hàn Quốc'},
	{value: 'JP', label: 'Nhật Bản'},
	{value: 'ASIA', label: 'Châu Á'},
	{value: 'SEA', label: 'Đông Nam Á'},
];

/**
 * Trang công cụ Google Trends: xu hướng tìm kiếm đang thịnh hành theo quốc gia/vùng
 * (feed RSS công khai của Google, BE cache chung 15 phút theo geo). Thanh tab trên
 * cùng = các vùng (VN, Trung Quốc, Hàn Quốc, Nhật Bản, Châu Á, Đông Nam Á — vùng
 * gộp nhiều nước, mỗi từ khoá gắn nhãn nước nguồn), nút gạt "Hôm nay / Lịch sử" ở
 * góc phải. Chọn từ khoá → lưu vào Kho từ khoá của 1 dự án.
 */
function TrendsHome() {

	const {notification} = AntdApp.useApp();
	const navigate = useNavigate();

	// Vùng đang xem (= key tab active) + chế độ xem (hôm nay / lịch sử).
	const [geo, setGeo] = useState('VN');
	const [view, setView] = useState('today');

	const {data, isFetching, error, refetch} = useGetTrendsQuery(geo);

	// Modal lưu kho: danh sách item đang chờ lưu (null = đóng).
	const [saving, setSaving] = useState(null);

	// Modal biểu đồ tần suất trend của 1 từ khoá (null = đóng).
	const [viewing, setViewing] = useState(null);

	const tabs = data?.tabs || DEFAULT_TABS;
	const items = data?.items || [];

	const handleSave = (rows) => {
		if (rows?.length) setSaving(rows);
	};

	// AI gợi ý góc bài viết bắt trend — sang /ai với task + input prefill (navigate state).
	const handleSuggest = (row) => {
		navigate('/ai', {
			state: {
				task: 'trend_content',
				input: {
					keyword: row.keyword,
					news: (row.news || []).map((n) => n.title).join('\n'),
				},
			},
		});
	};

	// Báo lỗi qua notification (key cố định — refetch lỗi lặp không spam nhiều toast).
	useEffect(() => {
		if (error) {
			notification.error({
				key: 'trends-error',
				message: 'Lỗi',
				description: rtkErrorMessage(error, 'Không lấy được dữ liệu Google Trends'),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error]);

	// Nội dung của vùng đang chọn — hôm nay hoặc lịch sử (chỉ pane active được mount
	// nhờ destroyInactiveTabPane nên luôn khớp geo/dữ liệu của query hiện tại).
	const renderPane = () => (
		view === 'history'
			? <TrendsHistoryPanel geo={geo} onSave={handleSave} />
			: (
				<div>
					{data?.stale && (
						<Alert
							type="warning" showIcon style={{marginBottom: 12}}
							message="Google tạm thời chặn yêu cầu — đang hiển thị dữ liệu gần nhất còn lưu."
						/>
					)}
					<TrendsTable
						items={items}
						loading={isFetching}
						onSave={handleSave}
						onSuggest={handleSuggest}
						onShowHistory={(row) => setViewing(row)}
					/>
					{data?.fetched_at && (
						<p style={{color: '#9ca3af', fontSize: 12, marginTop: 8}}>
							Cập nhật lúc {data.fetched_at} — dữ liệu làm mới tối đa mỗi 15 phút.
						</p>
					)}
				</div>
			)
	);

	const tabItems = tabs.map((t) => ({
		key: t.value,
		label: t.label,
		children: renderPane(),
	}));

	return (
		<div className="container">
			<PageHeader
				icon="fa-light fa-arrow-trend-up"
				title="Google Trends"
				subtitle="Xu hướng tìm kiếm thịnh hành theo quốc gia & khu vực — lưu từ khoá hot vào kho để bắt trend nội dung"
				actions={
					<Button white loading={isFetching} onClick={() => refetch()}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-rotate-right" />}>
						Làm mới
					</Button>
				}
			/>

			<div className="app-card" style={{padding: 16}}>
				<Tabs
					activeKey={geo}
					onChange={(k) => setGeo(k)}
					items={tabItems}
					destroyInactiveTabPane
					tabBarExtraContent={
						<Segmented
							value={view}
							onChange={setView}
							options={[
								{value: 'today', label: <span><FontAwesomeIcon icon="fa-light fa-fire" /> Hôm nay</span>},
								{value: 'history', label: <span><FontAwesomeIcon icon="fa-light fa-clock-rotate-left" /> Lịch sử</span>},
							]}
						/>
					}
				/>
			</div>

			<TrendsSaveModal
				open={!!saving}
				keywords={saving || []}
				geo={geo}
				onCancel={() => setSaving(null)}
				onSaved={() => setSaving(null)}
			/>

			<TrendsKeywordModal
				open={!!viewing}
				keyword={viewing?.keyword}
				hash={viewing?.hash}
				geo={geo}
				onCancel={() => setViewing(null)}
			/>
		</div>
	);
}

export default TrendsHome;
